import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ENDPOINT } from '../api/auth';
import { getLowStockProducts, getCategories, getLowStockProductsByCategory } from '../api/products';
import Modal from 'react-modal';
import { FaBox, FaMoneyBillWave, FaUsers, FaChartLine, FaShoppingCart, FaExchangeAlt, FaTruck, FaExclamationTriangle, FaUserCog } from 'react-icons/fa';
import { Bar, Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import './Dashboard.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('today');
  const [lowStockOpen, setLowStockOpen] = useState(false);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [lowStockLoading, setLowStockLoading] = useState(false);
  const [lowStockError, setLowStockError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [allLowStockItems, setAllLowStockItems] = useState([]);
  const [userStats, setUserStats] = useState(null);

  // Unpaid transactions states
  const [unpaidOpen, setUnpaidOpen] = useState(false);
  const [unpaidTransactions, setUnpaidTransactions] = useState([]);
  const [loadingUnpaid, setLoadingUnpaid] = useState(false);
  const [unpaidError, setUnpaidError] = useState(null);
  const [partialAmounts, setPartialAmounts] = useState({});
  const [partialErrors, setPartialErrors] = useState({});
  const [paymentMethods, setPaymentMethods] = useState({}); // txId -> 'cash' | 'bank'
  const [selectedBanks, setSelectedBanks] = useState({}); // txId -> bank name
  const banks = ['CBE', 'Awash', 'Dashen', 'Abyssinia', 'Birhan', 'Telebirr', 'Check'];

  // Fetch dashboard stats using analytics endpoint
  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Get current date range based on timeRange
      const today = new Date();
      let startDate, endDate;
      
      switch (timeRange) {
        case 'today':
          startDate = endDate = today.toISOString().split('T')[0];
          break;
        case 'week':
          const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
          const endOfWeek = new Date(today.setDate(today.getDate() - today.getDay() + 6));
          startDate = startOfWeek.toISOString().split('T')[0];
          endDate = endOfWeek.toISOString().split('T')[0];
          break;
        case 'month':
        default:
          startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
          endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
          break;
      }
      
      // Fetch analytics data
      const analyticsResponse = await axios.get(`${ENDPOINT}?action=getAnalyticsAndReports&start_date=${startDate}&end_date=${endDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Fetch basic stats (products, users, low stock)
      const basicStatsResponse = await axios.get(`${ENDPOINT}?action=get_dashboard_stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Fetch user stats if admin
      let userStatsData = null;
      if (user?.role === 'admin') {
        try {
          const userStatsResponse = await axios.get(`${ENDPOINT}?action=get_users`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (userStatsResponse.data.success) {
            const users = userStatsResponse.data.users || [];
            userStatsData = {
              total: users.length,
              admins: users.filter(u => u.role === 'admin').length,
              users: users.filter(u => u.role === 'user').length,
              workers: users.filter(u => u.role === 'worker').length,
              active: users.filter(u => u.is_active).length
            };
          }
        } catch (err) {
          console.warn('Failed to fetch user stats:', err);
        }
      }

      // Fetch correct low stock count using the same API as mobile
      const lowStockResponse = await axios.get(`${ENDPOINT}?action=getProductsLowStock`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let correctLowStockCount = 0;
      if (lowStockResponse?.data?.success) {
        const allProducts = lowStockResponse.data.products || [];
        const lowStockItems = allProducts.filter(product => product.low_stock_alert);
        correctLowStockCount = lowStockItems.length;
      }

      if (analyticsResponse.data.success && basicStatsResponse.data.success) {
        const analytics = analyticsResponse.data.summary;
        const basicStats = basicStatsResponse.data.stats;
        
        const transformedData = {
          total_products: basicStats.totalProducts || 0,
          today_sales: timeRange === 'today' ? analytics.total_sales || 0 : basicStats.todaySales || 0,
          week_sales: timeRange === 'week' ? analytics.total_sales || 0 : basicStats.weekSales || 0,
          month_sales: timeRange === 'month' ? analytics.total_sales || 0 : basicStats.monthSales || 0,
          total_sales: analytics.total_sales || 0,
          net_income: analytics.net_income || 0,
          total_profit: analytics.total_profit || 0,
          total_expenses: analytics.total_expenses || 0,
          total_credit: analytics.total_credit || 0,
          total_customers: basicStats.activeUsers || 0,
          low_stock_items: correctLowStockCount, // Use the correct filtered count
          recent_activities: basicStats.recentActivities || [],
          cash_balance: analytics.cash_balance || 0,
          bank_balances: analytics.bank_balances || {},
          category_sales: analytics.category_sales || {},
          charts: basicStatsResponse.data.charts || {
            daily: { labels: [], data: [] },
            weekly: { labels: [], data: [] },
            monthly: { labels: [], data: [] }
          },
          sales_over_time: basicStatsResponse.data.charts?.daily || { labels: [], data: [] },
          payment_methods: {}
        };
        setStats(transformedData);
        setUserStats(userStatsData);
      } else {
        throw new Error('Failed to load dashboard data');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
  }, [timeRange]);

  // ---- Unpaid Transactions logic ----
  const fetchUnpaidTransactions = async () => {
    try {
      setUnpaidError(null);
      setLoadingUnpaid(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${ENDPOINT}?action=get_unpaid_transactions`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res?.data?.success && Array.isArray(res.data.transactions)) {
        const txs = res.data.transactions
          .map(tx => ({ ...tx, unpaid_amount: parseFloat(tx.unpaid_amount || 0) }))
          .filter(tx => tx.unpaid_amount > 0);
        setUnpaidTransactions(txs);
        // initialize default methods/banks
        const initMethods = {};
        const initBanks = {};
        txs.forEach(tx => { initMethods[tx.id] = 'cash'; initBanks[tx.id] = 'CBE'; });
        setPaymentMethods(initMethods);
        setSelectedBanks(initBanks);
      } else {
        setUnpaidError(res?.data?.message || 'Failed to load unpaid transactions');
      }
    } catch (e) {
      setUnpaidError(e.response?.data?.message || 'Failed to load unpaid transactions');
    } finally {
      setLoadingUnpaid(false);
    }
  };

  const openUnpaidModal = async () => {
    await fetchUnpaidTransactions();
    setUnpaidOpen(true);
  };

  const updatePaymentMethod = (txId, method) => {
    setPaymentMethods(prev => ({ ...prev, [txId]: method }));
  };
  const updateSelectedBank = (txId, bank) => {
    setSelectedBanks(prev => ({ ...prev, [txId]: bank }));
  };

  const handlePayUnpaid = async (transactionId, amount, method, bankName) => {
    try {
      setUnpaidError(null);
      const token = localStorage.getItem('token');
      const payload = { transaction_id: transactionId, amount, payment_method: method };
      if (method === 'bank' && bankName) payload.bank_name = bankName;
      const res = await axios.post(`${ENDPOINT}?action=pay_unpaid_amount`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res?.data?.success) {
        // Update local list
        setUnpaidTransactions(prev => prev
          .map(tx => tx.id === transactionId ? { ...tx, unpaid_amount: (tx.unpaid_amount - amount) } : tx)
          .filter(tx => (tx.unpaid_amount || 0) > 0)
        );
        setPartialAmounts(prev => ({ ...prev, [transactionId]: '' }));
        setPartialErrors(prev => ({ ...prev, [transactionId]: null }));
      } else {
        setUnpaidError(res?.data?.message || 'Payment failed');
      }
    } catch (e) {
      setUnpaidError(e.response?.data?.message || 'Payment failed');
    }
  };

  const handlePayPartial = (tx) => {
    const amountStr = (partialAmounts[tx.id] || '').trim();
    const amt = parseFloat(amountStr);
    if (!amountStr) {
      setPartialErrors(prev => ({ ...prev, [tx.id]: 'Enter amount' }));
      return;
    }
    if (isNaN(amt) || amt <= 0) {
      setPartialErrors(prev => ({ ...prev, [tx.id]: 'Invalid amount' }));
      return;
    }
    if (amt > (tx.unpaid_amount || 0)) {
      setPartialErrors(prev => ({ ...prev, [tx.id]: 'Exceeds unpaid' }));
      return;
    }
    const method = paymentMethods[tx.id] || 'cash';
    const bank = method === 'bank' ? (selectedBanks[tx.id] || 'CBE') : undefined;
    handlePayUnpaid(tx.id, amt, method, bank);
  };

  const handlePayFull = (tx) => {
    const method = paymentMethods[tx.id] || 'cash';
    const bank = method === 'bank' ? (selectedBanks[tx.id] || 'CBE') : undefined;
    handlePayUnpaid(tx.id, tx.unpaid_amount || 0, method, bank);
  };

  // Ensure modal root is set
  useEffect(() => {
    Modal.setAppElement('#root');
  }, []);

  const fetchLowStock = async (categoryFilter = '') => {
    try {
      setLowStockLoading(true);
      
      // Use the same API endpoint as mobile version
      const token = localStorage.getItem('token');
      const response = await axios.get(`${ENDPOINT}?action=getProductsLowStock`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response?.data?.success) {
        const allProducts = response.data.products || [];
        
        // Filter products that have low stock alert (same as mobile version)
        const lowStockItems = allProducts.filter(product => product.low_stock_alert);
        setAllLowStockItems(lowStockItems);
        
        // Extract unique categories from the filtered low stock products
        const uniqueCategories = new Set();
        lowStockItems.forEach(product => {
          const category = product.category;
          if (category && category.trim()) {
            uniqueCategories.add(category.trim());
          }
        });
        
        // Convert to category objects
        const categoryList = Array.from(uniqueCategories).map(cat => ({
          id: cat,
          name: cat
        }));
        setCategories(categoryList);
        
        // Filter items based on category if specified
        if (categoryFilter) {
          const filtered = lowStockItems.filter(product => {
            const productCategory = product.category || '';
            return productCategory.trim() === categoryFilter;
          });
          setLowStockItems(filtered);
        } else {
          setLowStockItems(lowStockItems);
        }
      } else {
        setLowStockError(response?.data?.message || 'Failed to fetch low stock products');
      }
    } catch (e) {
      setLowStockError(e.response?.data?.message || 'Failed to fetch low stock products');
    } finally {
      setLowStockLoading(false);
    }
  };

  const openLowStockModal = async () => {
    try {
      setLowStockError(null);
      setSelectedCategory('');
      // Fetch all low stock products and extract categories from them
      await fetchLowStock('');
      setLowStockOpen(true);
    } catch (e) {
      setLowStockError(e.response?.data?.message || 'Failed to fetch low stock products');
      setLowStockOpen(true);
    }
  };

  // Calculate total sales based on time range
  const totalSales = useMemo(() => {
    if (!stats) return 0;
    switch (timeRange) {
      case 'today': return stats.today_sales;
      case 'week': return stats.week_sales;
      case 'month': return stats.month_sales;
      default: return 0;
    }
  }, [stats, timeRange]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Get chart data based on selected time range
  const getChartData = () => {
    let chartData = {
      labels: [],
      data: []
    };

    if (!stats) return chartData;

    switch (timeRange) {
      case 'today':
        chartData = {
          labels: stats?.charts?.daily?.labels || [],
          data: stats?.charts?.daily?.data || []
        };
        break;
      case 'week':
        chartData = {
          labels: stats?.charts?.weekly?.labels || [],
          data: stats?.charts?.weekly?.data || []
        };
        break;
      case 'month':
        chartData = {
          labels: stats?.charts?.monthly?.labels || [],
          data: stats?.charts?.monthly?.data || []
        };
        break;
      default:
        chartData = {
          labels: stats?.sales_over_time?.labels || [],
          data: stats?.sales_over_time?.data || []
        };
    }

    return chartData;
  };

  // Prepare chart data
  const chartData = getChartData();
  const salesChartData = {
    labels: chartData.labels,
    datasets: [
      {
        label: `Sales (${timeRange === 'today' ? 'Today' : timeRange === 'week' ? 'This Week' : 'This Month'})`,
        data: chartData.data,
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        pointBackgroundColor: 'rgba(54, 162, 235, 1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(54, 162, 235, 1)'
      }
    ]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('en-ET', {
                style: 'currency',
                currency: 'ETB'
              }).format(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return new Intl.NumberFormat('en-ET', {
              style: 'currency',
              currency: 'ETB',
              maximumFractionDigits: 0
            }).format(value);
          }
        }
      }
    }
  };

  const paymentMethodData = {
    labels: stats?.payment_methods ? Object.keys(stats.payment_methods) : [],
    datasets: [
      {
        data: stats?.payment_methods ? Object.values(stats.payment_methods) : [],
        backgroundColor: [
          'rgba(75, 192, 192, 0.6)',
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(153, 102, 255, 0.6)'
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(153, 102, 255, 1)'
        ],
        borderWidth: 1
      }
    ]
  };

  // Stats cards data
  const statCards = [
    {
      title: 'Total Products',
      value: stats?.total_products || 0,
      icon: <FaBox className="stat-icon" />,
      color: '#4e73df',
      trend: stats?.product_trend || 0
    },
    {
      title: 'Total Sales',
      value: formatCurrency(totalSales),
      icon: <FaMoneyBillWave className="stat-icon" />,
      color: '#1cc88a',
      trend: stats?.sales_trend || 0
    },
    {
      title: 'Net Income',
      value: formatCurrency(stats?.net_income || 0),
      icon: <FaChartLine className="stat-icon" />,
      color: '#28a745',
      trend: 0
    },
    {
      title: user?.role === 'admin' && userStats ? 'System Users' : 'Total Users',
      value: user?.role === 'admin' && userStats ? userStats.total : (stats?.total_customers || 0),
      icon: <FaUsers className="stat-icon" />,
      color: '#36b9cc',
      trend: stats?.customer_trend || 0,
      subtitle: user?.role === 'admin' && userStats ? `${userStats.admins} Admins, ${userStats.users} Users, ${userStats.workers} Workers` : null,
      clickable: user?.role === 'admin',
      onClick: user?.role === 'admin' ? () => navigate('/user-management') : undefined
    },
    {
      title: 'Total Credit',
      value: formatCurrency(stats?.total_credit || 0),
      icon: <FaExchangeAlt className="stat-icon" />,
      color: '#f6c23e',
      trend: 0
    },
    {
      title: 'Low Stock Items',
      value: stats?.low_stock_items || 0,
      icon: <FaExclamationTriangle className="stat-icon" />,
      color: '#e74a3b',
      trend: 0
    }
  ];

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-icon">⚠️</div>
        <h3>Error Loading Dashboard</h3>
        <p>{error}</p>
        <button onClick={fetchDashboardStats} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <div className="time-range-selector">
          <button 
            className={`time-btn ${timeRange === 'today' ? 'active' : ''}`}
            onClick={() => setTimeRange('today')}
          >
            Today
          </button>
          <button 
            className={`time-btn ${timeRange === 'week' ? 'active' : ''}`}
            onClick={() => setTimeRange('week')}
          >
            This Week
          </button>
          <button 
            className={`time-btn ${timeRange === 'month' ? 'active' : ''}`}
            onClick={() => setTimeRange('month')}
          >
            This Month
          </button>
        </div>
      </div>

      <div className="welcome-banner">
        <div className="welcome-content">
          <h2>Welcome back, {user?.username || 'User'}! 👋</h2>
          <p>Here's what's happening with your store today.</p>
          {user?.role && (
            <p className="user-role-badge" style={{
              display: 'inline-block',
              padding: '0.25rem 0.75rem',
              borderRadius: '12px',
              fontSize: '0.8rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              backgroundColor: user.role === 'admin' ? '#fef3c7' : user.role === 'user' ? '#dbeafe' : '#d1fae5',
              color: user.role === 'admin' ? '#92400e' : user.role === 'user' ? '#1e40af' : '#065f46',
              marginTop: '0.5rem'
            }}>
              {user.role}
            </p>
          )}
        </div>
        <div className="quick-actions">
          <button 
            className="action-btn"
            onClick={() => navigate('/sales')}
          >
            <FaShoppingCart /> New Sale
          </button>
          <button 
            className="action-btn"
            onClick={() => navigate('/product-order')}
          >
            <FaTruck /> Add Inventory
          </button>
          <button
            className="action-btn"
            onClick={openUnpaidModal}
          >
            💳 Unpaid
          </button>
          {user?.role === 'admin' && (
            <button
              className="action-btn"
              onClick={() => navigate('/user-management')}
            >
              <FaUserCog /> Manage Users
            </button>
          )}
        </div>
      </div>

      <div className="stats-grid">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="stat-card"
            style={{ 
              borderLeft: `4px solid ${stat.color}`, 
              cursor: (stat.title === 'Low Stock Items' || stat.clickable) ? 'pointer' : 'default' 
            }}
            onClick={stat.title === 'Low Stock Items' ? openLowStockModal : stat.onClick}
          >
            <div className="stat-icon-container" style={{ backgroundColor: `${stat.color}20` }}>
              {stat.icon}
            </div>
            <div className="stat-content">
              <p className="stat-title">{stat.title}</p>
              <h3 className="stat-value">{stat.value}</h3>
              {stat.subtitle && (
                <p className="stat-subtitle" style={{ 
                  fontSize: '0.8rem', 
                  color: '#6b7280', 
                  margin: '0.25rem 0' 
                }}>
                  {stat.subtitle}
                </p>
              )}
              <div className={`stat-trend ${stat.trend >= 0 ? 'positive' : 'negative'}`}>
                {stat.trend >= 0 ? '↑' : '↓'} {Math.abs(stat.trend)}%
                <span>vs last period</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="charts-row">
        <div className="chart-container" style={{ height: '400px' }}>
          <h3>Sales Performance</h3>
          <Line data={salesChartData} options={chartOptions} />
        </div>

        <div className="chart-container" style={{ height: '400px' }}>
          <h3>Payment Methods</h3>
          <div className="chart-wrapper">
            <Pie 
              data={paymentMethodData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom'
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const label = context.label || '';
                        const value = context.raw || 0;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = Math.round((value / total) * 100);
                        return `${label}: ${formatCurrency(value)} (${percentage}%)`;
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>
      </div>

      <div className="recent-activity">
        <h3>Recent Activity</h3>
        <div className="activity-list">
          {stats?.recent_activities?.length > 0 ? (
            stats.recent_activities.map((activity, index) => (
              <div key={index} className="activity-item">
                <div className="activity-icon">
                  {activity.type === 'sale' ? '💰' : 
                   activity.type === 'purchase' ? '🛒' : '📝'}
                </div>
                <div className="activity-details">
                  <p className="activity-description">{activity.description}</p>
                  <p className="activity-time">{activity.time}</p>
                </div>
                <div className="activity-amount">
                  {activity.amount && formatCurrency(activity.amount)}
                </div>
              </div>
            ))
          ) : (
            <p className="no-activity">No recent activities found</p>
          )}
        </div>
      </div>

      {/* Low Stock Modal */}
      <Modal
        isOpen={lowStockOpen}
        onRequestClose={() => setLowStockOpen(false)}
        className="modal"
        overlayClassName="overlay"
      >
        <h2>Low Stock Products</h2>
        {/* Category Filter */}
        <div className="filter-row" style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '8px 0 16px' }}>
          <label htmlFor="lowstock-category" style={{ fontWeight: 600 }}>Category:</label>
          <select
            id="lowstock-category"
            value={selectedCategory}
            onChange={async (e) => {
              const val = e.target.value;
              setSelectedCategory(val);
              setLowStockError(null);
              await fetchLowStock(val);
            }}
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        {lowStockLoading && <p>Loading...</p>}
        {lowStockError && <p className="error-text">{lowStockError}</p>}
        {!lowStockLoading && !lowStockError && (
          <div className="table-responsive">
            {lowStockItems.length === 0 ? (
              <p>No products are currently at or below their low stock threshold.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Current Stock</th>
                    <th>Low Stock Threshold</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockItems.map((p) => (
                    <tr key={p.id}>
                      <td>{p.name}</td>
                      <td>{p.category_name || p.category || '—'}</td>
                      <td>{p.current_stock}</td>
                      <td>{p.low_stock}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        <div className="actions" style={{ marginTop: 16 }}>
          <button onClick={() => setLowStockOpen(false)}>Close</button>
          <button onClick={() => { setLowStockOpen(false); navigate('/products'); }}>Go to Products</button>
        </div>
      </Modal>

      {/* Unpaid Transactions Modal */}
      <Modal
        isOpen={unpaidOpen}
        onRequestClose={() => setUnpaidOpen(false)}
        className="modal"
        overlayClassName="overlay"
      >
        <h2>Unpaid Transactions</h2>
        {!loadingUnpaid && !unpaidError && unpaidTransactions.length > 0 && (
          <div style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '12px 16px', 
            borderRadius: '8px', 
            marginBottom: '16px',
            border: '1px solid #e9ecef'
          }}>
            <div style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#e74c3c',
              textAlign: 'center'
            }}>
              Total Unpaid Amount: ETB {unpaidTransactions.reduce((sum, tx) => sum + (parseFloat(tx.unpaid_amount) || 0), 0).toFixed(2)}
            </div>
          </div>
        )}
        {loadingUnpaid && <p>Loading...</p>}
        {unpaidError && <p className="error-text">{unpaidError}</p>}
        {!loadingUnpaid && !unpaidError && (
          <div className="table-responsive">
            {unpaidTransactions.length === 0 ? (
              <p>No unpaid transactions found</p>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {unpaidTransactions.map((tx) => (
                  <div key={tx.id} className="card" style={{ padding: 12, border: '1px solid #eee', borderRadius: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div>
                        <strong>{tx.customer_name || 'Customer'}</strong>
                        <div style={{ fontSize: 12, color: '#666' }}>{new Date(tx.transaction_date).toLocaleString()}</div>
                      </div>
                      <div style={{ color: '#e74c3c', fontWeight: 700 }}>ETB {(tx.unpaid_amount || 0).toFixed(2)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600 }}>Method:</span>
                      <button
                        className={`action-btn method-btn ${ (paymentMethods[tx.id] || 'cash') === 'cash' ? 'active' : ''}`}
                        onClick={() => updatePaymentMethod(tx.id, 'cash')}
                      >Cash</button>
                      <button
                        className={`action-btn method-btn ${ (paymentMethods[tx.id] || 'cash') === 'bank' ? 'active' : ''}`}
                        onClick={() => updatePaymentMethod(tx.id, 'bank')}
                      >Bank</button>
                      {(paymentMethods[tx.id] || 'cash') === 'bank' && (
                        <select
                          value={selectedBanks[tx.id] || 'CBE'}
                          onChange={(e) => updateSelectedBank(tx.id, e.target.value)}
                        >
                          {banks.map(b => (<option key={b} value={b}>{b}</option>))}
                        </select>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                      <button className="action-btn pay-btn" onClick={() => handlePayPartial(tx)}>Pay Partial</button>
                      <button className="action-btn pay-btn" onClick={() => handlePayFull(tx)}>Pay Full</button>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                      <span>ETB</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Enter amount for partial payment"
                        value={partialAmounts[tx.id] || ''}
                        onChange={(e) => {
                          setPartialAmounts(prev => ({ ...prev, [tx.id]: e.target.value }));
                          setPartialErrors(prev => ({ ...prev, [tx.id]: null }));
                        }}
                        style={{ flex: 1 }}
                      />
                    </div>
                    {partialErrors[tx.id] && (
                      <div style={{ color: '#e74a3b', fontSize: 12, marginTop: 4 }}>{partialErrors[tx.id]}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="actions" style={{ marginTop: 16 }}>
          <button onClick={() => setUnpaidOpen(false)}>Close</button>
        </div>
      </Modal>
    </div>
  );
};

export default Dashboard;
